import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, filter, take, takeUntil } from 'rxjs/operators';
import { MultiSelectModule } from 'primeng/multiselect';
import { ChartModule } from 'primeng/chart';
import { ToggleSwitch } from 'primeng/toggleswitch';
import {
  DashboardActiveProjectDetails,
  DashboardActiveProjectListItem,
  DashboardEquipmentProgressProjectSeries,
  DashboardEquipmentProgressResult,
  DashboardManHoursResult,
  DashboardManHoursSeries,
  DashboardRecurringFailureMode,
  DashboardRecurringFailureModesResult,
  DashboardCustomerProgressSummary,
  DashboardCustomerTemplateSummary,
  DashboardProjectCommissioningProgress,
  DashboardProjectSkidStatusSummary
} from '../../models/dashboard.model';
import { BusinessUnitModel } from '../../models/template.model';
import { DashboardService } from '../../services/dashboard.service';
import { SSOService } from '../../services/sso.service';
import { TemplateService } from '../../services/template.service';
import { AppFeatureStateService } from '../../services/app-feature-state.service';

type DashboardDateRangeTarget = 'manHours' | 'equipment' | 'failureModes';
type DashboardDateRangeField = 'startDate' | 'endDate';

@Component({
  selector: 'app-dashboard-home',
  imports: [CommonModule, FormsModule, MultiSelectModule, ChartModule, ToggleSwitch],
  templateUrl: './dashboard-home.html',
  styleUrl: './dashboard-home.css'
})
export class DashboardHome implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private currentUserId: number | null = null;

  loading = true;
  errorMessage = '';

  activeProjects: DashboardActiveProjectListItem[] = [];
  integrationSummary: DashboardProjectSkidStatusSummary[] = [];
  customerSkidSummary: DashboardCustomerProgressSummary[] = [];
  commissioningSummary: DashboardProjectCommissioningProgress[] = [];
  equipmentProgress: DashboardEquipmentProgressProjectSeries[] = [];
  recurringFailureModes: DashboardRecurringFailureMode[] = [];
  manHoursLabels: string[] = [];
  manHoursSeries: DashboardManHoursSeries[] = [];
  manHoursYAxis: number[] = [0];
  manHoursChartData: any = { labels: [], datasets: [] };
  manHoursChartOptions: any = {};
  equipmentChartData: any = { labels: [], datasets: [] };
  equipmentChartOptions: any = {};
  selectedManHoursCategories: string[] = [];

  projectDetailsById: Record<number, DashboardActiveProjectDetails> = {};
  detailLoadingById: Record<number, boolean> = {};
  detailErrorById: Record<number, string> = {};

  expandedActiveProjectId: number | null = null;
  expandedIntegrationProjectId: number | null = null;
  expandedCustomerProjectId: number | null = null;
  expandedCustomerTemplateIdByProject: Record<number, number | null> = {};
  expandedCommissionProjectId: number | null = null;
  expandedFailureModeKey: string | null = null;
  showActiveProjects = true;
  activeDateRangeTarget: DashboardDateRangeTarget | null = null;
  dateRangeDraft: { startDate: string; endDate: string } = { startDate: '', endDate: '' };
  dateRangeError = '';
  selectedEquipmentTemplateGroups: string[] = [];
  businessUnitOptions: { id: number; displayName: string }[] = [];
  selectedBusinessUnitId: number | null = null;
  appliedDateRanges: Record<DashboardDateRangeTarget, { startDate: string; endDate: string }> = {
    manHours: { startDate: '', endDate: '' },
    equipment: { startDate: '', endDate: '' },
    failureModes: { startDate: '', endDate: '' }
  };

  private readonly equipmentPalette = ['#68748f', '#e0a104', '#ef3b67', '#63ae6f', '#9aa2b3', '#3d5b83', '#7a67c7', '#5d8f95'];
  private readonly manHoursPalette = ['#3d5b83', '#ef3b67', '#63ae6f', '#9aa2b3', '#7a67c7', '#e0a104', '#5d8f95', '#68748f'];

  constructor(
    private dashboardService: DashboardService,
    private ssoService: SSOService,
    private templateService: TemplateService,
    private appFeatureState: AppFeatureStateService
  ) {
    this.appliedDateRanges.manHours = this.getCurrentMonthDateRange();
    this.manHoursChartOptions = this.buildManHoursChartOptions();
    this.equipmentChartOptions = this.buildEquipmentChartOptions();
  }

  canView(featureKey: string): boolean {
    return this.appFeatureState.canView(featureKey);
  }

  ngOnInit(): void {
    const current = this.ssoService.getCurrentUser();
    if (current?.userId) {
      this.currentUserId = current.userId;
      this.loadBusinessUnitsAndDashboard(current.userId);
      return;
    }

    this.ssoService.user$
      .pipe(
        filter((user) => !!user?.userId),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe((user) => {
        if (!user?.userId) {
          this.loading = false;
          this.errorMessage = 'Unable to identify current user.';
          return;
        }
        this.currentUserId = user.userId;
        this.loadBusinessUnitsAndDashboard(user.userId);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByProjectId = (_: number, item: DashboardActiveProjectListItem): number => item.projectId;

  toggleActiveProject(projectId: number): void {
    this.expandedActiveProjectId = this.expandedActiveProjectId === projectId ? null : projectId;
    if (this.expandedActiveProjectId === projectId) {
      this.loadProjectDetails(projectId);
    }
  }

  toggleActiveProjectsSection(): void {
    this.showActiveProjects = !this.showActiveProjects;
    if (!this.showActiveProjects) {
      this.expandedActiveProjectId = null;
    }
  }

  toggleIntegrationProject(projectId: number): void {
    this.expandedIntegrationProjectId = this.expandedIntegrationProjectId === projectId ? null : projectId;
  }

  toggleCustomerProject(projectId: number): void {
    const closing = this.expandedCustomerProjectId === projectId;
    this.expandedCustomerProjectId = closing ? null : projectId;
    if (closing) {
      delete this.expandedCustomerTemplateIdByProject[projectId];
    }
  }

  toggleCustomerTemplate(projectId: number, projectTemplateId: number): void {
    const current = this.expandedCustomerTemplateIdByProject[projectId] ?? null;
    this.expandedCustomerTemplateIdByProject = {
      ...this.expandedCustomerTemplateIdByProject,
      [projectId]: current === projectTemplateId ? null : projectTemplateId
    };
  }

  toggleCommissionProject(projectId: number): void {
    this.expandedCommissionProjectId = this.expandedCommissionProjectId === projectId ? null : projectId;
  }

  getFailureModeKey(failureMode: DashboardRecurringFailureMode, index: number): string {
    return `${failureMode.problemCodeDetails}|${index}`;
  }

  toggleFailureModeRow(failureMode: DashboardRecurringFailureMode, index: number): void {
    const key = this.getFailureModeKey(failureMode, index);
    this.expandedFailureModeKey = this.expandedFailureModeKey === key ? null : key;
  }

  isFailureModeExpanded(failureMode: DashboardRecurringFailureMode, index: number): boolean {
    return this.expandedFailureModeKey === this.getFailureModeKey(failureMode, index);
  }

  formatReportedOn(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  getIntegrationRows(projectId: number): { label: string; count: number }[] {
    const project = this.integrationSummary.find((x) => x.projectId === projectId);
    const rows = project?.statusCounts ?? [];
    if (!rows.length) {
      return [{ label: 'Not Started', count: 0 }];
    }
    return rows.map((row) => ({ label: row.status, count: row.count }));
  }

  getCustomerTemplates(projectId: number): DashboardCustomerTemplateSummary[] {
    const project = this.customerSkidSummary.find((x) => x.projectId === projectId);
    return project?.templates ?? [];
  }

  getCustomerRows(projectId: number, projectTemplateId: number): { label: string; count: number }[] {
    const template = this.getCustomerTemplates(projectId).find((x) => x.projectTemplateId === projectTemplateId);
    const rows = template?.statusCounts ?? [];
    if (!rows.length) {
      return [{ label: 'Draft', count: 0 }];
    }
    return rows.map(row => ({ label: row.status, count: row.count }));
  }

  getCommissionRows(projectId: number): { label: string; value: string }[] {
    const project = this.commissioningSummary.find((x) => x.projectId === projectId);
    const groups = project?.groups ?? [];
    if (!groups.length) {
      return [{ label: 'No commissioning templates', value: '0/0' }];
    }
    return groups.map((group) => ({
      label: group.groupName,
      value: `${group.verified}/${group.total}`
    }));
  }

  getCommissionProgressPercent(value: string): number {
    const [verifiedRaw, totalRaw] = (value ?? '').split('/');
    const verified = Number((verifiedRaw ?? '').trim());
    const total = Number((totalRaw ?? '').trim());

    if (!Number.isFinite(verified) || !Number.isFinite(total) || total <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, (verified / total) * 100));
  }

  formatSkidConfiguration(value?: string | null): string {
    const normalized = (value ?? '').trim();
    if (!normalized) {
      return '-';
    }

    const parts = normalized.split('-').map(x => x.trim());
    if (parts.length !== 7) {
      return normalized;
    }

    const mapped = parts
      .map((raw, index) => ({ index: index + 1, count: Number(raw) }))
      .filter(x => Number.isFinite(x.count) && x.count > 0)
      .map(x => `N${x.index}:${x.count}`);

    return mapped.length > 0 ? mapped.join('|') : '-';
  }

  getSafeValue(value?: string | null): string {
    const normalized = (value ?? '').trim();
    return normalized ? normalized : '-';
  }

  isGiProject(detail: DashboardActiveProjectDetails): boolean {
    const businessUnit = (detail.businessUnitName ?? '').trim().toUpperCase();
    return businessUnit === 'GI' || businessUnit.startsWith('GI ') || businessUnit.includes('(GI)');
  }

  getDetail(projectId: number): DashboardActiveProjectDetails | null {
    return this.projectDetailsById[projectId] ?? null;
  }

  getProjectNumbers(project: DashboardActiveProjectListItem): string {
    const eq = this.getSafeValue(project.projectNumber);
    const cx = this.getSafeValue(project.commProject);
    const wr = this.getSafeValue(project.warrantyProject);
    return `EQ: ${eq} | CX: ${cx} | WR: ${wr}`;
  }

  getCollapsedSiteManager(project: DashboardActiveProjectListItem): string {
    const manager = (project.siteManager ?? '').trim();
    if (manager) {
      return manager;
    }
    const fieldLead = (project.fieldLead ?? '').trim();
    if (fieldLead) {
      return fieldLead;
    }
    const detail = this.getDetail(project.projectId);
    const fieldLeads = detail?.fieldLeads?.filter(x => (x || '').trim().length > 0) ?? [];
    return fieldLeads.length ? fieldLeads.join(', ') : '-';
  }

  getProjectStatusIndicatorClass(project: DashboardActiveProjectListItem): string {
    const indicator = (project.projectStatusIndicator ?? '').trim().toLowerCase();
    if (indicator === 'green') {
      return 'project-status-indicator--green';
    }
    if (indicator === 'yellow') {
      return 'project-status-indicator--yellow';
    }
    return '';
  }

  getProjectStatusIndicatorLabel(project: DashboardActiveProjectListItem): string {
    const indicator = (project.projectStatusIndicator ?? '').trim().toLowerCase();
    if (indicator === 'green') {
      return 'Daily report submitted in the last 4 days';
    }
    if (indicator === 'yellow') {
      return 'Active resources on project; no daily report in the last 4 days';
    }
    return '';
  }

  getPercent(value: number): string {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${Math.round(safeValue)}%`;
  }

  getStatusClass(status: 'Open' | 'Resolved' | 'Investigating' | 'In Progress'): string {
    switch (status) {
      case 'Open':
        return 'status-open';
      case 'Resolved':
        return 'status-resolved';
      case 'Investigating':
        return 'status-investigating';
      default:
        return 'status-in-progress';
    }
  }

  onBusinessUnitChange(): void {
    if (!this.currentUserId) {
      return;
    }
    this.loadDashboard(this.currentUserId);
  }

  setBusinessUnit(businessUnitId: number): void {
    if (this.selectedBusinessUnitId === businessUnitId) {
      return;
    }
    this.selectedBusinessUnitId = businessUnitId;
    this.onBusinessUnitChange();
  }

  toggleDateRange(target: DashboardDateRangeTarget): void {
    if (this.activeDateRangeTarget === target) {
      this.closeDateRange();
      return;
    }

    this.activeDateRangeTarget = target;
    const selected = this.appliedDateRanges[target];
    this.dateRangeDraft = {
      startDate: selected.startDate,
      endDate: selected.endDate
    };
    this.dateRangeError = '';
  }

  isDateRangeOpen(target: DashboardDateRangeTarget): boolean {
    return this.activeDateRangeTarget === target;
  }

  onDateRangeInput(field: DashboardDateRangeField, value: string): void {
    this.dateRangeDraft = {
      ...this.dateRangeDraft,
      [field]: value
    };
    this.dateRangeError = '';
  }

  canApplyDateRange(): boolean {
    const { startDate, endDate } = this.dateRangeDraft;
    return !!startDate && !!endDate && startDate <= endDate;
  }

  applyDateRange(): void {
    const target = this.activeDateRangeTarget;
    if (!target) {
      return;
    }

    const { startDate, endDate } = this.dateRangeDraft;
    if (!startDate || !endDate) {
      this.dateRangeError = 'Start and end date are required.';
      return;
    }

    if (startDate > endDate) {
      this.dateRangeError = 'Start date must be before end date.';
      return;
    }

    this.appliedDateRanges[target] = { startDate, endDate };
    this.closeDateRange();

    if (target === 'manHours') {
      this.loadManHours();
    }
  }

  closeDateRange(): void {
    this.activeDateRangeTarget = null;
    this.dateRangeError = '';
  }

  getDateRangeLabel(target: DashboardDateRangeTarget): string {
    const selected = this.appliedDateRanges[target];
    if (!selected.startDate || !selected.endDate) {
      return 'Date Range';
    }

    return `${this.formatDateLabel(selected.startDate)} - ${this.formatDateLabel(selected.endDate)}`;
  }

  getFormattedManHoursLabels(): string[] {
    return this.manHoursLabels.map(label => this.formatDateLabel(label));
  }

  getManHoursSeriesColor(category: string): string {
    const names = this.manHoursSeries.map(series => series.category);
    const index = Math.max(0, names.indexOf(category));
    return this.manHoursPalette[index % this.manHoursPalette.length];
  }

  getManHoursCategoryOptions(): Array<{ label: string; value: string }> {
    return this.manHoursSeries.map(item => ({ label: item.category, value: item.category }));
  }

  getVisibleManHoursSeries(): DashboardManHoursSeries[] {
    if (this.selectedManHoursCategories.length === 0) {
      return this.manHoursSeries;
    }

    const selected = new Set(this.selectedManHoursCategories);
    return this.manHoursSeries.filter(item => selected.has(item.category));
  }

  onManHoursCategorySelectionChange(): void {
    this.refreshManHoursChartData();
  }

  getEquipmentProjects(): DashboardEquipmentProgressProjectSeries[] {
    return this.equipmentProgress;
  }

  getEquipmentTemplateGroups(): string[] {
    const groups = new Set<string>();
    this.equipmentProgress.forEach(project => {
      project.values.forEach(value => {
        const key = (value.templateShortCode || '').trim();
        if (key) {
          groups.add(key);
        }
      });
    });
    return Array.from(groups);
  }

  getEquipmentTemplateOptions(): Array<{ label: string; value: string }> {
    return this.getEquipmentTemplateGroups().map(groupName => ({
      label: groupName,
      value: groupName
    }));
  }

  onEquipmentTemplateSelectionChange(): void {
    this.refreshEquipmentChartData();
  }

  downloadManHours(): void {
    this.dashboardService
      .downloadApplicationManHours(
        this.appliedDateRanges.manHours.startDate || undefined,
        this.appliedDateRanges.manHours.endDate || undefined,
        this.selectedBusinessUnitId ?? undefined,
        this.selectedManHoursCategories
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.buildHomeExportFileName('Man_Hours')}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.errorMessage = 'Failed to download man hours report.';
        }
      });
  }

  downloadEquipmentProgress(): void {
    this.dashboardService
      .downloadApplicationEquipmentProgress(
        this.selectedBusinessUnitId ?? undefined,
        this.selectedEquipmentTemplateGroups
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.buildHomeExportFileName('Equipment_Progress')}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.errorMessage = 'Failed to download equipment progress report.';
        }
      });
  }

  downloadRecurringFailureModes(): void {
    this.dashboardService
      .downloadApplicationRecurringFailureModes(this.selectedBusinessUnitId ?? undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${this.buildHomeExportFileName('Recurring_Failure_Modes')}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.errorMessage = 'Failed to download recurring failure modes report.';
        }
      });
  }

  getEquipmentColor(groupName: string): string {
    const groups = this.getEquipmentTemplateGroups();
    const index = Math.max(0, groups.indexOf(groupName));
    return this.equipmentPalette[index % this.equipmentPalette.length];
  }

  getEquipmentProjectLabel(projectName: string): string {
    const normalized = (projectName || '').trim();
    if (!normalized) {
      return '-';
    }
    return normalized;
  }

  getEquipmentChartMinWidthPx(): number {
    const projectCount = Math.max(1, this.equipmentProgress.length);
    return Math.max(960, projectCount * 120);
  }

  private formatDateLabel(value: string): string {
    const parts = value.split('-');
    if (parts.length !== 3) {
      return value;
    }

    const year = Number(parts[0]);
    const month = Number(parts[1]) - 1;
    const day = Number(parts[2]);
    const parsed = new Date(year, month, day);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  private updateManHoursChart(manHours: DashboardManHoursResult | null | undefined): void {
    const labels = [...(manHours?.labels ?? [])];
    const series = [...(manHours?.series ?? [])]
      .map(item => ({
        category: (item.category ?? '').trim(),
        hours: Array.isArray(item.hours) ? item.hours.map(value => Number(value) || 0) : [],
        totalHours: Number(item.totalHours) || 0
      }))
      .filter(item => item.category.length > 0 && item.hours.length > 0);

    this.manHoursLabels = labels;
    this.manHoursSeries = series;

    const available = new Set(this.manHoursSeries.map(item => item.category));
    this.selectedManHoursCategories = this.selectedManHoursCategories.filter(category => available.has(category));
    this.refreshManHoursChartData();
  }

  private buildManHoursYAxis(maxValue: number): number[] {
    if (!Number.isFinite(maxValue) || maxValue <= 0) {
      return [0];
    }

    const paddedMax = maxValue * 1.1;
    const step = Math.max(1, Math.ceil(this.roundManHoursStep(paddedMax / 5)));
    const top = Math.max(step * 5, Math.ceil(paddedMax / step) * step);

    return Array.from({ length: 6 }, (_, index) => top - (index * step));
  }

  private roundManHoursStep(rawStep: number): number {
    const safe = Math.max(0.5, rawStep);
    if (safe <= 1) {
      return 1;
    }
    if (safe <= 2) {
      return 2;
    }
    if (safe <= 5) {
      return 5;
    }
    if (safe <= 10) {
      return 10;
    }

    return Math.ceil(safe / 5) * 5;
  }

  private buildManHoursChartOptions(): any {
    const yTicks = this.manHoursYAxis;
    const yMax = yTicks.length > 0 ? yTicks[0] : 0;
    const yStep = yTicks.length > 1 ? yTicks[0] - yTicks[1] : 1;

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context: any) => `${context.dataset.label}: ${context.parsed.y}h`
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#787e91',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          },
          border: {
            color: '#e9e9ed'
          }
        },
        y: {
          beginAtZero: true,
          min: 0,
          max: yMax > 0 ? yMax : undefined,
          ticks: {
            stepSize: yStep > 0 ? yStep : 1,
            precision: 0,
            color: '#787e91',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          },
          grid: {
            color: '#e9e9ed'
          },
          border: {
            display: false
          }
        }
      }
    };
  }

  private buildEquipmentChartOptions(): any {
    const toTwoLineLabel = (rawLabel: string): string | string[] => {
      const label = (rawLabel || '').trim();
      if (!label) {
        return '-';
      }

      const maxLineLength = 22;
      if (label.length <= maxLineLength) {
        return label;
      }

      const normalized = label.replace(/\s+/g, ' ');
      const midpoint = Math.floor(normalized.length / 2);
      let splitAt = normalized.lastIndexOf(' ', midpoint);

      if (splitAt < 8) {
        splitAt = normalized.indexOf(' ', midpoint);
      }
      if (splitAt < 8) {
        splitAt = maxLineLength;
      }

      const first = normalized.slice(0, splitAt).trim();
      let second = normalized.slice(splitAt).trim();
      if (second.length > maxLineLength) {
        second = `${second.slice(0, maxLineLength - 1).trimEnd()}...`;
      }

      return [first, second];
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'nearest',
        intersect: true
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            title: (items: any[]) => items?.[0]?.label ?? '',
            label: (context: any) => {
              const dataIndex = Number(context.dataIndex ?? 0);
              const templateNames: string[] = context.dataset?.metaTemplateNames ?? [];
              const template = (templateNames?.[dataIndex] ?? '').trim() || 'Template';
              const kind = context.dataset?.metaKind ?? 'Value';
              const value = Number(context.parsed?.y ?? 0);
              return `${template} ${kind}: ${value}`;
            },
            footer: (items: any[]) => {
              const first = items?.[0];
              if (!first) {
                return '';
              }

              const dataIndex = Number(first.dataIndex ?? 0);
              const totals: number[] = first.dataset?.metaTotals ?? [];
              const verifieds: number[] = first.dataset?.metaVerifieds ?? [];
              const total = Number(totals?.[dataIndex] ?? 0);
              const verified = Number(verifieds?.[dataIndex] ?? 0);
              return `Verified: ${verified}/${total}`;
            }
          }
        }
      },
      scales: {
        x: {
          offset: true,
          grid: {
            display: false,
            offset: true
          },
          ticks: {
            color: '#787e91',
            maxRotation: 0,
            minRotation: 0,
            autoSkip: false,
            callback: function (value: any) {
              const label = String((this as any).getLabelForValue(value) ?? '');
              return toTwoLineLabel(label);
            },
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          },
          border: {
            color: '#e9e9ed'
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0,
            stepSize: 5,
            color: '#787e91',
            font: {
              family: 'Lato, Segoe UI, Arial, sans-serif',
              size: 11
            }
          },
          grid: {
            color: '#e9e9ed'
          },
          border: {
            display: false
          }
        }
      }
    };
  }

  private refreshEquipmentChartData(): void {
    const projects = this.equipmentProgress ?? [];
    const labels = projects.map(project => this.getEquipmentProjectLabel(project.projectName));
    const selected = new Set(this.selectedEquipmentTemplateGroups);
    const filteredValuesByProject = projects.map(project => {
      const values = (project.values ?? [])
        .filter(v => {
          const key = (v.templateShortCode || '').trim();
          if (!key) {
            return false;
          }
          return selected.size === 0 || selected.has(key);
        });
      return values;
    });

    const maxSlots = filteredValuesByProject.reduce((max, values) => Math.max(max, values.length), 0);

    const datasets: any[] = [];
    for (let slotIndex = 0; slotIndex < maxSlots; slotIndex++) {
      const verifiedData: Array<number | null> = [];
      const unverifiedData: Array<number | null> = [];
      const verifiedColors: string[] = [];
      const unverifiedColors: string[] = [];
      const templateNames: string[] = [];
      const totals: number[] = [];
      const verifieds: number[] = [];

      for (let projectIndex = 0; projectIndex < projects.length; projectIndex++) {
        const value = filteredValuesByProject[projectIndex][slotIndex];
        if (!value) {
          verifiedData.push(null);
          unverifiedData.push(null);
          verifiedColors.push('rgba(0,0,0,0)');
          unverifiedColors.push('rgba(0,0,0,0)');
          templateNames.push('');
          totals.push(0);
          verifieds.push(0);
          continue;
        }

        const groupName = (value.templateShortCode || '').trim();
        const color = this.getEquipmentColor(groupName);
        const total = Math.max(0, Number(value.totalEquipments ?? 0));
        const verified = Math.max(0, Math.min(total, Number(value.totalVerified ?? 0)));
        const unverified = Math.max(0, total - verified);

        verifiedData.push(verified);
        unverifiedData.push(unverified);
        verifiedColors.push(color);
        unverifiedColors.push(this.toTransparentColor(color, 0.18));
        templateNames.push(groupName);
        totals.push(total);
        verifieds.push(verified);
      }

      const stackId = `slot-${slotIndex}`;
      datasets.push({
        label: `Slot ${slotIndex + 1} Verified`,
        stack: stackId,
        data: verifiedData,
        backgroundColor: verifiedColors,
        borderColor: verifiedColors,
        borderWidth: 0,
        borderRadius: 3,
        barThickness: 12,
        maxBarThickness: 12,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
        metaTemplateNames: templateNames,
        metaKind: 'Verified',
        metaTotals: totals,
        metaVerifieds: verifieds
      });

      datasets.push({
        label: `Slot ${slotIndex + 1} Not Verified`,
        stack: stackId,
        data: unverifiedData,
        backgroundColor: unverifiedColors,
        borderColor: unverifiedColors,
        borderWidth: 0,
        borderRadius: 3,
        barThickness: 12,
        maxBarThickness: 12,
        categoryPercentage: 0.8,
        barPercentage: 0.9,
        metaTemplateNames: templateNames,
        metaKind: 'Not Verified',
        metaTotals: totals,
        metaVerifieds: verifieds
      });
    }

    this.equipmentChartData = {
      labels,
      datasets
    };

    const maxTotal = projects
      .flatMap(project => project.values.map(value => Math.max(0, Number(value.totalEquipments ?? 0))))
      .reduce((max, value) => Math.max(max, value), 0);

    const step = maxTotal <= 20 ? 5 : 10;
    const suggestedMax = maxTotal > 0 ? Math.ceil(maxTotal / step) * step : 10;

    const baseOptions = this.buildEquipmentChartOptions();
    this.equipmentChartOptions = {
      ...baseOptions,
      scales: {
        ...baseOptions.scales,
        y: {
          ...baseOptions.scales.y,
          max: suggestedMax,
          ticks: {
            ...baseOptions.scales.y.ticks,
            stepSize: step
          }
        }
      }
    };
  }

  private refreshManHoursChartData(): void {
    const visibleSeries = this.getVisibleManHoursSeries();
    const maxValue = visibleSeries.length > 0
      ? Math.max(...visibleSeries.flatMap(item => item.hours))
      : 0;

    this.manHoursYAxis = this.buildManHoursYAxis(maxValue);

    this.manHoursChartData = {
      labels: this.getFormattedManHoursLabels(),
      datasets: visibleSeries.map((item) => ({
        label: item.category,
        data: item.hours,
        borderColor: this.getManHoursSeriesColor(item.category),
        backgroundColor: this.toTransparentColor(this.getManHoursSeriesColor(item.category), 0.16),
        pointBackgroundColor: this.getManHoursSeriesColor(item.category),
        pointBorderColor: this.getManHoursSeriesColor(item.category),
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
        tension: 0,
        fill: true
      }))
    };

    this.manHoursChartOptions = this.buildManHoursChartOptions();
  }

  private toTransparentColor(hexColor: string, alpha: number): string {
    const value = hexColor.replace('#', '');
    if (value.length !== 6) {
      return `rgba(61, 91, 131, ${alpha})`;
    }

    const r = parseInt(value.substring(0, 2), 16);
    const g = parseInt(value.substring(2, 4), 16);
    const b = parseInt(value.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  private getCurrentMonthDateRange(): { startDate: string; endDate: string } {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const format = (value: Date): string => {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: format(start),
      endDate: format(end)
    };
  }

  private loadManHours(): void {
    const selected = this.appliedDateRanges.manHours;

    this.dashboardService.getMyManHours(
      selected.startDate || undefined,
      selected.endDate || undefined,
      this.selectedBusinessUnitId ?? undefined)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of({
          granularity: 'day',
          labels: [],
          series: [],
          totals: { allHours: 0 }
        } as DashboardManHoursResult))
      )
      .subscribe((result) => {
        this.updateManHoursChart(result);
      });
  }

  private loadDashboard(userId: number): void {
    this.loading = true;
    this.errorMessage = '';
    this.expandedActiveProjectId = null;
    this.projectDetailsById = {};
    this.detailLoadingById = {};
    this.detailErrorById = {};

    const businessUnitId = this.selectedBusinessUnitId ?? undefined;

    forkJoin({
      projects: this.dashboardService.getMyActiveProjects(businessUnitId).pipe(catchError(() => of([]))),
      integration: this.dashboardService.getProjectSkidStatusSummaryByUser(userId, undefined, businessUnitId).pipe(catchError(() => of([]))),
      customerSkid: this.dashboardService.getCustomerSkidStatusSummaryList(businessUnitId).pipe(catchError(() => of([]))),
      commissioning: this.dashboardService.getMyCommissioningProgress(undefined, businessUnitId).pipe(catchError(() => of([]))),
      equipment: this.dashboardService.getMyEquipmentProgress(businessUnitId).pipe(catchError(() => of({ series: [] } as DashboardEquipmentProgressResult))),
      failureModes: this.dashboardService.getMyRecurringFailureModes(businessUnitId).pipe(catchError(() => of({ failureModes: [] } as DashboardRecurringFailureModesResult))),
      manHours: this.dashboardService.getMyManHours(
        this.appliedDateRanges.manHours.startDate || undefined,
        this.appliedDateRanges.manHours.endDate || undefined,
        businessUnitId
      ).pipe(catchError(() => of({
        granularity: 'day',
        labels: [],
        series: [],
        totals: { allHours: 0 }
      } as DashboardManHoursResult)))
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ projects, integration, customerSkid, commissioning, equipment, failureModes, manHours }) => {
          this.activeProjects = [...projects].sort((a, b) => b.projectId - a.projectId);
          this.integrationSummary = [...integration].sort((a, b) => b.projectId - a.projectId);
          this.customerSkidSummary = [...customerSkid].sort((a, b) => b.projectId - a.projectId);
          this.commissioningSummary = [...commissioning].sort((a, b) => b.projectId - a.projectId);
          this.equipmentProgress = [...(equipment?.series ?? [])].sort((a, b) => b.projectId - a.projectId);
          this.recurringFailureModes = [...(failureModes?.failureModes ?? [])];
          this.updateManHoursChart(manHours);
          this.expandedFailureModeKey = null;
          const validGroups = new Set(this.getEquipmentTemplateGroups());
          this.selectedEquipmentTemplateGroups = this.selectedEquipmentTemplateGroups.filter(group => validGroups.has(group));
          this.refreshEquipmentChartData();
          this.initializeExpandedSections();
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.errorMessage = 'Failed to load dashboard data.';
        }
      });
  }

  private loadBusinessUnitsAndDashboard(userId: number): void {
    this.templateService.getBusinessUnits()
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of([] as BusinessUnitModel[]))
      )
      .subscribe((units) => {
        this.businessUnitOptions = (units || []).map((unit) => ({
          id: unit.id,
          displayName: unit.displayName || unit.name
        }));

        if (this.businessUnitOptions.length > 0) {
          const defaultUnit = this.businessUnitOptions.find((x) => x.id === 1) ?? this.businessUnitOptions[0];
          this.selectedBusinessUnitId = defaultUnit.id;
        } else {
          this.selectedBusinessUnitId = null;
        }

        this.loadDashboard(userId);
      });
  }

  private initializeExpandedSections(): void {
    if (this.activeProjects.length === 0) {
      this.expandedActiveProjectId = null;
      this.expandedIntegrationProjectId = null;
      this.expandedCustomerProjectId = null;
      this.expandedCommissionProjectId = null;
      return;
    }

    const firstId = this.activeProjects[0].projectId;
    this.expandedActiveProjectId = null;
    this.expandedIntegrationProjectId = firstId;
    this.expandedCustomerProjectId = firstId;
    this.expandedCommissionProjectId = firstId;
  }

  private loadProjectDetails(projectId: number): void {
    if (this.projectDetailsById[projectId] || this.detailLoadingById[projectId]) {
      return;
    }

    this.detailLoadingById[projectId] = true;
    this.detailErrorById[projectId] = '';

    this.dashboardService.getMyActiveProjectDetails(projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (detail) => {
          this.projectDetailsById[projectId] = detail;
          this.detailLoadingById[projectId] = false;
        },
        error: () => {
          this.detailLoadingById[projectId] = false;
          this.detailErrorById[projectId] = 'Unable to load project details.';
        }
      });
  }

  private buildHomeExportFileName(reportName: string): string {
    const selectedUnit = this.businessUnitOptions.find((item) => item.id === this.selectedBusinessUnitId)?.displayName || 'All';
    return `Home_${reportName}_${selectedUnit}`;
  }
}

